import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ExternalLink, Copy, Check, Share2, Globe, Image as ImageIcon,
  NotebookPen, Flag, Eye, Loader2, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Switch } from '@/components/ui/switch';
import { ActionButton, SectionHeader, StatusBadge, CurrentStateLine } from './VisualAtoms';
import { TONE } from '../theme/adminVisualSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ['submitted', 'approved', 'hidden', 'archived'];
const STATUS_VARIANT = {
  submitted: 'cyan',
  approved: 'success',
  hidden: 'ghost',
  archived: 'danger',
};

function statusTone(s) {
  return ({
    submitted: TONE.cyan,
    approved: TONE.green,
    hidden: TONE.muted,
    archived: TONE.red,
  }[s] || TONE.neutral);
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function MetaRow({ label, value }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-2.5">
      <p className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">{label}</p>
      <p className="text-xs font-mono text-white mt-1 break-words">
        {value || <span className="text-[#444]">—</span>}
      </p>
    </div>
  );
}

function UsageRow({ icon: Icon, label, checked, onChange, disabled, testId }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] px-3 py-2.5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${checked ? 'text-[#90E0EF]' : 'text-[#666]'}`} />
        <span className={`text-xs font-mono ${checked ? 'text-white' : 'text-[#aaa]'}`}>{label}</span>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testId}
        className="data-[state=checked]:bg-[#90E0EF] data-[state=unchecked]:bg-[#222]"
      />
    </div>
  );
}

/**
 * ArtistWorkPreviewModal — large central modal that animates in from
 * the bottom. Replaces the right-side ArtistWorkDrawer.
 *
 * - Desktop: 75vw × 75vh centered, max-w-[1400px]
 * - Mobile:  100vw × 90vh bottom-sheet feel
 *
 * Heavy originals are NOT auto-loaded:
 *   - Image: rendered immediately (typically thumbnail-cached by Supabase CDN)
 *   - Video: shows "Load Preview" button, then preload="metadata" + controls
 */
export default function ArtistWorkPreviewModal({ work, onClose, onUpdate }) {
  const [current, setCurrent] = useState(work);
  const [busy, setBusy] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editArtistNotes, setEditArtistNotes] = useState('');
  const [editUsageNotes, setEditUsageNotes] = useState('');

  useEffect(() => {
    setCurrent(work);
    setPreviewLoaded(false);
    setEditTitle(work?.title || '');
    setEditDescription(work?.description || '');
    setEditArtistNotes(work?.artist_notes || '');
    setEditUsageNotes(work?.usage_notes || '');
  }, [work]);

  useEffect(() => {
    if (!work) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    // lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [work, onClose]);

  const open = !!work;
  const w = current;

  const patch = async (body) => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/admin/artist-works/${w.id}`, body);
      setCurrent(res.data);
      onUpdate?.(res.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const saveText = () => patch({
    title: editTitle,
    description: editDescription,
    artist_notes: editArtistNotes,
  });
  const saveUsageNotes = () => patch({ usage_notes: editUsageNotes });
  const toggleUsage = (key) => { if (!w) return; patch({ [key]: !w[key] }); };

  const copyUrl = async () => {
    if (!w?.file_url) return;
    try {
      await navigator.clipboard.writeText(w.file_url);
      setCopied(true);
      toast.success('URL copied');
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Could not copy');
    }
  };

  const isImage = w?.file_type === 'image';
  const isVideo = w?.file_type === 'video';
  const isAudio = w?.file_type === 'audio';
  const tone = statusTone(w?.status);

  return (
    <AnimatePresence>
      {open && w && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            data-testid="work-modal-backdrop"
          />

          {/* Centered modal */}
          <div className="fixed inset-0 z-[101] flex items-end sm:items-center justify-center p-0 sm:p-6 pointer-events-none">
            <motion.div
              key={w.id}
              initial={{ y: '100%', opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: '100%', opacity: 0, scale: 0.98 }}
              transition={{ type: 'tween', duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
              className="pointer-events-auto bg-[#070707] border border-[#1a1a1a] w-full h-[90vh] sm:w-[75vw] sm:max-w-[1400px] sm:h-[75vh] shadow-[0_-30px_80px_rgba(0,0,0,0.6),0_0_60px_rgba(144,224,239,0.06)] flex flex-col overflow-hidden"
              data-testid="artist-work-modal"
              role="dialog"
              aria-modal="true"
            >
              {/* Header */}
              <header className="border-b border-[#1a1a1a] bg-[#070707]/95 backdrop-blur-sm px-5 py-3 flex items-start justify-between gap-3 shrink-0">
                <div className="min-w-0">
                  <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">Artist Work</p>
                  <h2 className="font-outfit text-base sm:text-lg font-semibold text-white truncate mt-0.5">
                    {w.title || w.file_name || 'Untitled'}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <StatusBadge status={w.status || 'submitted'} strong />
                    <span className="text-[10px] font-mono px-2 py-0.5 border border-[#333] text-[#aaa] uppercase tracking-[0.12em]">
                      {(w.file_type || 'file').toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  data-testid="artist-work-modal-close"
                  className="w-8 h-8 border border-[#222] text-[#888] hover:text-white hover:border-[#90E0EF]/40 flex items-center justify-center transition-colors shrink-0"
                  data-clickable="true"
                  style={{ cursor: 'pointer' }}
                >
                  <X className="w-4 h-4" />
                </button>
              </header>

              {/* Body — split: 70% preview / 30% details on desktop, stacked on mobile */}
              <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">

                {/* Preview pane */}
                <div className="lg:w-[70%] bg-[#050505] flex items-center justify-center relative overflow-hidden border-b lg:border-b-0 lg:border-r border-[#1a1a1a] min-h-[40vh] lg:min-h-0">
                  {isImage ? (
                    // Images load directly — typically lighter than originals via Supabase CDN
                    <img
                      src={w.file_url}
                      alt={w.title || w.file_name}
                      className="max-w-full max-h-full object-contain"
                      data-testid="modal-preview-image"
                    />
                  ) : isVideo ? (
                    !previewLoaded ? (
                      <div className="text-center px-4 relative">
                        {w.thumbnail_url && (
                          <img src={w.thumbnail_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                        )}
                        <button
                          type="button"
                          onClick={() => setPreviewLoaded(true)}
                          data-clickable="true"
                          data-testid="modal-load-video-btn"
                          className="relative z-10 inline-flex items-center gap-2 px-4 py-2 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 text-[10px] font-mono uppercase tracking-[0.18em] transition-colors"
                          style={{ cursor: 'pointer' }}
                        >
                          <Eye className="w-3.5 h-3.5" /> Load Video
                        </button>
                      </div>
                    ) : (
                      <video
                        src={w.file_url}
                        controls
                        preload="metadata"
                        className="max-w-full max-h-full object-contain"
                        data-testid="modal-preview-video"
                      />
                    )
                  ) : isAudio ? (
                    <audio src={w.file_url} controls className="w-full max-w-md mx-6" />
                  ) : (
                    <div className="text-center px-4">
                      <p className="text-xs font-mono text-[#888]">No inline preview for this file type.</p>
                    </div>
                  )}
                </div>

                {/* Details pane — scrollable */}
                <div className="lg:w-[30%] overflow-y-auto p-5 space-y-6 bg-[#070707]" data-testid="artist-work-modal-details">

                  {/* Status */}
                  <section>
                    <SectionHeader icon={Flag} title="Status" helper="Curatorial review state." />
                    <div className="flex flex-wrap gap-1.5">
                      {STATUSES.map((s) => (
                        <ActionButton
                          key={s}
                          variant={STATUS_VARIANT[s]}
                          active={(w.status || 'submitted') === s}
                          disabled={busy}
                          onClick={() => patch({ status: s })}
                          dense
                          data-testid={`work-status-${s}`}
                        >
                          {s}
                        </ActionButton>
                      ))}
                    </div>
                    <CurrentStateLine label="Current" value={w.status || 'submitted'} tone={tone} />
                  </section>

                  {/* Internal Usage */}
                  <section>
                    <SectionHeader title="Internal Usage" helper="Curator-only classification. Never shown to the artist." />
                    <div className="space-y-2">
                      <UsageRow icon={Share2} label="Social Media" checked={!!w.usage_social_media} onChange={() => toggleUsage('usage_social_media')} disabled={busy} testId="usage-social" />
                      <UsageRow icon={ImageIcon} label="Gallery Exhibition" checked={!!w.usage_gallery_exhibition} onChange={() => toggleUsage('usage_gallery_exhibition')} disabled={busy} testId="usage-gallery" />
                      <UsageRow icon={Globe} label="Website" checked={!!w.usage_website} onChange={() => toggleUsage('usage_website')} disabled={busy} testId="usage-website" />
                      {w.usage_updated_at && (
                        <p className="text-[10px] font-mono text-[#666] mt-1">Usage updated · {fmt(w.usage_updated_at)}</p>
                      )}
                    </div>
                    <div className="mt-3">
                      <textarea
                        value={editUsageNotes}
                        onChange={(e) => setEditUsageNotes(e.target.value)}
                        placeholder="Usage notes (internal)"
                        rows={2}
                        data-testid="work-usage-notes-input"
                        className="w-full bg-transparent border border-[#222] text-white font-mono text-xs px-2.5 py-2 placeholder:text-[#555] focus:outline-none focus:border-[#90E0EF] resize-none"
                      />
                      <div className="mt-2">
                        <ActionButton variant="primary" onClick={saveUsageNotes} disabled={busy} icon={NotebookPen} data-testid="save-usage-notes-btn">
                          Save Notes
                        </ActionButton>
                      </div>
                    </div>
                  </section>

                  {/* Title / description / artist notes */}
                  <section>
                    <SectionHeader icon={NotebookPen} title="Title & Description" helper="Override what the artist provided." />
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder="Title"
                        data-testid="work-title-input"
                        className="w-full bg-transparent border border-[#222] text-white font-mono text-xs h-10 px-2.5 placeholder:text-[#555] focus:outline-none focus:border-[#90E0EF]"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                        rows={3}
                        data-testid="work-description-input"
                        className="w-full bg-transparent border border-[#222] text-white font-mono text-xs px-2.5 py-2 placeholder:text-[#555] focus:outline-none focus:border-[#90E0EF] resize-none"
                      />
                      <textarea
                        value={editArtistNotes}
                        onChange={(e) => setEditArtistNotes(e.target.value)}
                        placeholder="Artist notes"
                        rows={2}
                        data-testid="work-artist-notes-input"
                        className="w-full bg-transparent border border-[#222] text-white font-mono text-xs px-2.5 py-2 placeholder:text-[#555] focus:outline-none focus:border-[#90E0EF] resize-none"
                      />
                      <ActionButton
                        variant="primary"
                        onClick={saveText}
                        disabled={busy}
                        icon={busy ? Loader2 : NotebookPen}
                        data-testid="save-work-text-btn"
                      >
                        Save
                      </ActionButton>
                    </div>
                  </section>

                  {/* Metadata */}
                  <section>
                    <SectionHeader title="Metadata" helper="As reported on upload." />
                    <div className="grid grid-cols-2 gap-px">
                      <MetaRow label="Filename" value={w.file_name} />
                      <MetaRow label="Mime" value={w.mime_type} />
                      <MetaRow label="Size" value={formatBytes(w.file_size)} />
                      <MetaRow label="Type" value={w.file_type} />
                      <MetaRow label="Dimensions" value={w.width && w.height ? `${w.width} × ${w.height}` : '—'} />
                      <MetaRow label="Duration" value={w.duration_seconds ? `${Math.round(w.duration_seconds)}s` : '—'} />
                      <MetaRow label="Uploaded" value={fmt(w.created_at)} />
                      <MetaRow label="Reviewed" value={fmt(w.reviewed_at)} />
                    </div>
                  </section>

                  {/* Original file actions */}
                  <section>
                    <SectionHeader title="Original File" helper="Direct access to the uploaded asset." />
                    <div className="flex flex-wrap gap-2">
                      <ActionButton variant="secondary" onClick={() => window.open(w.file_url, '_blank', 'noopener')} icon={ExternalLink} disabled={!w.file_url} data-testid="open-original-btn">
                        Open
                      </ActionButton>
                      <ActionButton variant="secondary" onClick={copyUrl} icon={copied ? Check : Copy} disabled={!w.file_url} data-testid="copy-original-url-btn">
                        {copied ? 'Copied' : 'Copy URL'}
                      </ActionButton>
                      <a
                        href={w.file_url}
                        download={w.file_name || 'work'}
                        data-clickable="true"
                        data-testid="download-original-btn"
                        className="inline-flex items-center justify-center gap-2 font-mono uppercase tracking-[0.14em] border rounded-none transition-all duration-200 text-[11px] px-3 py-2 text-[#d6d6d6] border-[#2a2a2a] hover:border-[#90E0EF]/40 hover:text-[#90E0EF]"
                        style={{ cursor: 'pointer' }}
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </div>
                  </section>
                </div>
              </div>

              {/* Footer */}
              <footer className="border-t border-[#1a1a1a] px-5 py-2 flex items-center justify-between text-[10px] font-mono text-[#555] shrink-0">
                <span>ESC to close</span>
                <span className="truncate ml-4">ID · {w.id}</span>
              </footer>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
